import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useText } from "../contexts/TextsContext";
import { Building2, Menu, X, Sun, Moon, ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { trackPhoneClick } from "../lib/tracking";
import TrustStrip from "./TrustStrip";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { branding, theme, menu, dark, setDark } = useTheme();
  const t = useText();
  const [open, setOpen] = useState(false);
  const [openSub, setOpenSub] = useState(null);
  const [dynPages, setDynPages] = useState([]);
  const loc = useLocation();
  const isAdmin = loc.pathname.startsWith("/admin");

  useEffect(() => {
    if (!isAdmin) api.get("/pages").then(r => setDynPages(r.data)).catch(() => {});
  }, [isAdmin]);

  const headerLinks = (menu.header_links && menu.header_links.length > 0)
    ? menu.header_links
    : [
        { label: "Accueil", url: "/", children: [] },
        { label: "Nos locaux", url: "/locaux", children: [] },
        { label: "À propos", url: "/a-propos", children: [] },
        { label: "Blog", url: "/blog", children: [] },
        { label: "Contact", url: "/contact", children: [] },
      ];

  const dynLinks = (dynPages || []).map(p => ({ label: p.title, url: `/p/${p.slug}`, children: [] }));
  const allLinks = [...headerLinks, ...dynLinks];

  const logo = branding.logo_main_url;
  const logoMobile = branding.logo_mobile_url || logo;
  const name = branding.site_name || "Immobilier Solaire";
  const showDark = theme.dark_mode_enabled !== false;

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-xl border-b" style={{ background: "color-mix(in srgb, var(--bg-surface) 90%, transparent)", borderColor: "var(--border-default)" }} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          {logo ? (
            <img src={logo} alt={name} className="h-10 max-w-[180px] object-contain" />
          ) : (
            <>
              <div className="w-10 h-10 bg-[color:var(--solar)] border border-slate-900 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-slate-900" strokeWidth={2.5} />
              </div>
              <div className="leading-tight">
                {name.split(" ").length > 1 ? (
                  <>
                    <div className="font-display font-black text-base tracking-tight uppercase">{name.split(" ")[0]}</div>
                    <div className="font-display font-black text-base tracking-tight -mt-1 uppercase">{name.split(" ").slice(1).join(" ")}<span className="text-[color:var(--solar)]">.</span></div>
                  </>
                ) : (
                  <div className="font-display font-black text-base tracking-tight uppercase">{name}<span className="text-[color:var(--solar)]">.</span></div>
                )}
              </div>
            </>
          )}
        </Link>

        <div className="hidden lg:flex items-center gap-5">
          {!isAdmin && allLinks.map((l, idx) => (
            l.children && l.children.length > 0 ? (
              <div key={idx} className="relative group" onMouseEnter={() => setOpenSub(idx)} onMouseLeave={() => setOpenSub(null)}>
                <button className="text-sm font-semibold uppercase tracking-wider flex items-center gap-1" style={{ color: "var(--text-secondary)" }}>
                  {l.label} <ChevronDown className="w-3 h-3" />
                </button>
                {openSub === idx && (
                  <div className="absolute top-full left-0 mt-2 min-w-[200px] border" style={{ background: "var(--bg-surface)", borderColor: "var(--border-default)" }}>
                    {l.children.map((c, ci) => (
                      <Link key={ci} to={c.url} className="block px-4 py-2 text-sm hover:bg-[color:var(--solar)]" style={{ color: "var(--text-primary)" }}>{c.label}</Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <NavLink key={idx} to={l.url} end={l.url === "/"}
                data-testid={`nav-${(l.url || "home").replace(/\//g,"")}`}
                className={({isActive}) => `text-sm font-semibold uppercase tracking-wider transition-colors`}
                style={({isActive}) => ({ color: isActive ? "var(--text-primary)" : "var(--text-secondary)" })}>
                {l.label}
              </NavLink>
            )
          ))}
          {isAdmin && user && (
            <>
              <span className="text-xs font-mono" style={{ color: "var(--text-secondary)" }}>{user.name} · <span className="uppercase" style={{ color: "var(--solar)" }}>{user.role}</span></span>
              <button onClick={logout} data-testid="logout-btn" className="text-sm font-semibold uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>Déconnexion</button>
            </>
          )}
          {!isAdmin && showDark && (
            <button onClick={() => setDark(!dark)} data-testid="dark-toggle" className="w-9 h-9 border border-slate-900 flex items-center justify-center hover:bg-[color:var(--solar)]" style={{ color: "var(--text-primary)" }} title={dark ? "Mode clair" : "Mode sombre"}>
              {dark ? <Sun className="w-4 h-4"/> : <Moon className="w-4 h-4"/>}
            </button>
          )}
          {!isAdmin && (
            <Link to="/locaux" data-testid="nav-cta-reserve" className="btn-brutal btn-brutal-solar px-5 py-3 text-sm font-bold uppercase tracking-wider">{t("nav.cta_reserve")}</Link>
          )}
        </div>

        <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" style={{ color: "var(--text-primary)" }}>
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t" style={{ background: "var(--bg-surface)", borderColor: "var(--border-default)" }}>
          <div className="px-6 py-4 flex flex-col gap-4">
            {allLinks.map((l, idx) => (
              <div key={idx}>
                <Link to={l.url} onClick={() => setOpen(false)} className="text-sm font-semibold uppercase tracking-wider" style={{ color: "var(--text-primary)" }}>{l.label}</Link>
                {l.children?.map((c, ci) => (
                  <Link key={ci} to={c.url} onClick={() => setOpen(false)} className="block pl-4 mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>↳ {c.label}</Link>
                ))}
              </div>
            ))}
            {!isAdmin && showDark && (
              <button onClick={() => setDark(!dark)} className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2" style={{ color: "var(--text-primary)" }}>
                {dark ? <Sun className="w-4 h-4"/> : <Moon className="w-4 h-4"/>} {dark ? "Mode clair" : "Mode sombre"}
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export const Footer = () => {
  const { branding, menu } = useTheme();
  const t = useText();
  const cols = menu.footer_columns && menu.footer_columns.length > 0 ? menu.footer_columns : [];
  const socials = menu.socials || [];
  const name = branding.site_name || "Immobilier Solaire";
  const slogan = branding.site_slogan || "Locaux d'activité industriels modulables.";
  const footerLogo = branding.logo_footer_url || branding.logo_main_url;

  return (
    <footer className="border-t border-slate-900/10 bg-slate-900 text-slate-100 mt-32 pb-16 lg:pb-0" data-testid="footer">
      <TrustStrip />      <div className="max-w-7xl mx-auto px-6 md:px-8 py-16 grid md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          {footerLogo ? (
            <img src={footerLogo} alt={name} className="h-12 max-w-[200px] object-contain mb-4" />
          ) : (
            <div className="font-display font-black text-2xl mb-4 uppercase">{name}<span className="text-[color:var(--solar)]">.</span></div>
          )}
          <p className="text-slate-400 max-w-md text-sm leading-relaxed">{slogan}</p>
          {socials.length > 0 && (
            <div className="flex gap-3 mt-6">
              {socials.map((s, i) => (
                <a key={i} href={s.url} target="_blank" rel="noreferrer" className="border border-slate-700 px-3 py-1 text-xs uppercase tracking-wider hover:border-[color:var(--solar)]">{s.type}</a>
              ))}
            </div>
          )}
        </div>
        {cols.length > 0 ? cols.map((col, i) => (
          <div key={i}>
            <div className="text-xs uppercase tracking-widest text-[color:var(--solar)] font-bold mb-4">{col.title}</div>
            <ul className="space-y-2 text-sm">
              {col.links?.map((l, li) => (
                <li key={li}><Link to={l.url} className="hover:text-[color:var(--solar)]">{l.label}</Link></li>
              ))}
            </ul>
          </div>
        )) : (
          <>
            <div>
              <div className="text-xs uppercase tracking-widest text-[color:var(--solar)] font-bold mb-4">{t("footer.address_title")}</div>
              <p className="text-sm text-slate-300 leading-relaxed">98 Route Nationale 20<br/>45520 Cercottes<br/>France</p>
              <p className="text-sm text-slate-300 mt-3">Tél : <a href="tel:0238745060" onClick={() => trackPhoneClick("02 38 74 50 60")} className="hover:text-[color:var(--solar)]">02 38 74 50 60</a></p>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[color:var(--solar)] font-bold mb-4">{t("footer.links_title")}</div>
              <ul className="space-y-2 text-sm">
                <li><Link to="/locaux" className="hover:text-[color:var(--solar)]">Nos locaux</Link></li>
                <li><Link to="/blog" className="hover:text-[color:var(--solar)]">Blog</Link></li>
                <li><Link to="/contact" className="hover:text-[color:var(--solar)]">Contact</Link></li>
                <li><Link to="/locataire/login" className="hover:text-[color:var(--solar)]">Espace locataire</Link></li>
                <li><Link to="/admin/login" className="hover:text-[color:var(--solar)] text-slate-500">{t("footer.admin_link")}</Link></li>
              </ul>
            </div>
          </>
        )}
      </div>
      <div className="border-t border-slate-800 py-6 text-center text-xs text-slate-500 font-mono">
        © {new Date().getFullYear()} {name} {t("footer.copyright_suffix")}
      </div>
    </footer>
  );
};
